import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Gift, Check, ArrowRight, Loader2, Heart, Star, PartyPopper, Cake, Users, Flower2, PenLine } from 'lucide-react';
import { SiteHeader, SiteFooter, WhatsAppButton } from './RestaurantSite';

const API = process.env.REACT_APP_BACKEND_URL;
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Manrope:wght@300;400;500;600;700&display=swap";

const OCCASION_ICONS = {
  'anniversaire': Cake,
  'saint-valentin': Heart,
  'noel': Star,
  'fete-des-meres': Flower2,
  'fete-des-peres': Users,
  'mariage': PartyPopper,
  'paques': Star,
  'remerciement': Heart,
};

// ══════ PURCHASE FORM ══════
function BonsCadeauxShop() {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({
    client_prenom: '',
    client_nom: '',
    client_email: '',
    beneficiaire_prenom: '',
    beneficiaire: '',
    message: '',
    occasion: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/bons-cadeaux/packages`)
      .then(r => r.json())
      .then(data => {
        setConfig(data);
        if (data.packages?.length > 1) setSelected(data.packages[1].id);
        else if (data.packages?.length > 0) setSelected(data.packages[0].id);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCustom = selected === 'custom';
    if (!selected && !isCustom) { setError('Veuillez choisir un montant'); return; }
    if (isCustom) {
      const amt = parseFloat(customAmount);
      if (!amt || amt < (config?.montant_min || 10) || amt > (config?.montant_max || 500)) {
        setError(`Montant entre ${config?.montant_min || 10}€ et ${config?.montant_max || 500}€`);
        return;
      }
    }
    if (!form.client_email || !form.client_email.includes('@')) { setError('Votre email est requis'); return; }
    setError('');
    setLoading(true);

    try {
      const body = {
        package_id: selected,
        origin_url: window.location.origin,
        ...form,
      };
      if (isCustom) body.custom_amount = parseFloat(customAmount);

      const res = await fetch(`${API}/api/bons-cadeaux/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#1a2744]" />
      </div>
    );
  }

  if (!config.en_ligne) {
    return (
      <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',Georgia,serif]" data-testid="bons-cadeaux-offline">
        <link href={FONT_LINK} rel="stylesheet" />
        <SiteHeader section="restaurant" />
        <div className="pt-24 pb-16 min-h-[70vh] flex items-center justify-center">
          <div className="text-center px-4">
            <Gift size={48} className="mx-auto mb-6 text-gray-300" />
            <h1 className="text-3xl font-light mb-3">Bons Cadeaux</h1>
            <p className="text-gray-400 text-base font-['Manrope',sans-serif]">
              La vente en ligne n'est pas disponible pour le moment.<br />
              Contactez-nous directement au restaurant.
            </p>
          </div>
        </div>
        <SiteFooter section="restaurant" />
      </div>
    );
  }

  const packages = config.packages || [];
  const occasions = config.occasions || [];
  const isCustom = selected === 'custom';
  const displayAmount = isCustom
    ? (parseFloat(customAmount) || 0)
    : (packages.find(p => p.id === selected)?.amount || 0);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',Georgia,serif]" data-testid="bons-cadeaux-public">
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      {/* Hero */}
      <div className="relative h-[45vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-[#1a2744]" />
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/images/restaurant/hero.jpg)" }} />
        <div className="relative z-10 text-center px-6">
          <p className="text-[#0d9488] text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-4">Restaurant Le Mas</p>
          <h1 className="text-4xl md:text-5xl font-light text-white mb-3">Bons Cadeaux</h1>
          <p className="text-white/50 text-base font-['Manrope',sans-serif] max-w-lg mx-auto">
            Offrez un moment convivial a vos proches
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-16">
        <form onSubmit={handleSubmit} className="space-y-10" data-testid="bon-cadeau-form">

          {/* Step 1: Amount */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-[#1a2744] text-white flex items-center justify-center text-sm font-['Manrope',sans-serif] font-bold">1</span>
              <h2 className="text-2xl font-light">Choisissez le montant</h2>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="package-selector">
              {packages.map(pkg => (
                <button key={pkg.id} type="button"
                  onClick={() => { setSelected(pkg.id); setCustomAmount(''); }}
                  className={`relative py-5 px-8 rounded-xl border-2 transition-all text-center ${
                    selected === pkg.id
                      ? 'border-[#1a2744] bg-[#1a2744]/5 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  <span className={`text-2xl font-light ${selected === pkg.id ? 'text-[#1a2744]' : 'text-gray-700'}`}>
                    {parseInt(pkg.amount)}
                  </span>
                  <span className={`text-lg ml-0.5 ${selected === pkg.id ? 'text-[#0d9488]' : 'text-gray-400'}`}>€</span>
                  {selected === pkg.id && (
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#0d9488] rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
              {config.montant_libre && (
                <button type="button"
                  onClick={() => setSelected('custom')}
                  className={`relative py-5 px-6 rounded-xl border-2 transition-all text-center flex items-center gap-2 ${
                    isCustom
                      ? 'border-[#1a2744] bg-[#1a2744]/5 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  data-testid="package-custom"
                >
                  <PenLine size={18} className={isCustom ? 'text-[#1a2744]' : 'text-gray-400'} />
                  <span className={`text-base font-['Manrope',sans-serif] ${isCustom ? 'text-[#1a2744] font-semibold' : 'text-gray-500'}`}>
                    Montant libre
                  </span>
                  {isCustom && (
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#0d9488] rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </button>
              )}
            </div>
            {isCustom && (
              <div className="mt-4 max-w-xs" data-testid="custom-amount-input">
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">
                  Montant ({config.montant_min}€ — {config.montant_max}€)
                </label>
                <div className="relative">
                  <input type="number" step="1" min={config.montant_min} max={config.montant_max}
                    value={customAmount} onChange={e => setCustomAmount(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-2xl font-light text-[#1a2744] font-['Cormorant_Garamond',serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20 pr-10"
                    placeholder={String(config.montant_min)}
                    autoFocus
                    data-testid="custom-amount-field"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#0d9488] text-xl">€</span>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Occasion */}
          {occasions.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-[#1a2744] text-white flex items-center justify-center text-sm font-['Manrope',sans-serif] font-bold">2</span>
                <h2 className="text-2xl font-light">L'occasion</h2>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="occasion-selector">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, occasion: '' }))}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-['Manrope',sans-serif] transition-all ${
                    !form.occasion
                      ? 'border-[#1a2744] bg-[#1a2744] text-white'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="occasion-none"
                >
                  <Gift size={14} /> Sans occasion
                </button>
                {occasions.map(o => {
                  const Icon = OCCASION_ICONS[o.value] || Gift;
                  return (
                    <button key={o.value} type="button"
                      onClick={() => setForm(f => ({ ...f, occasion: o.value }))}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-['Manrope',sans-serif] transition-all ${
                        form.occasion === o.value
                          ? 'border-[#1a2744] bg-[#1a2744] text-white'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`occasion-${o.value}`}
                    >
                      <Icon size={14} /> {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-[#1a2744] text-white flex items-center justify-center text-sm font-['Manrope',sans-serif] font-bold">{occasions.length > 0 ? '3' : '2'}</span>
              <h2 className="text-2xl font-light">Vos informations</h2>
            </div>

            {/* Acheteur */}
            <p className="text-xs font-['Manrope',sans-serif] text-gray-400 uppercase tracking-wider mb-3">Acheteur</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Prenom *</label>
                <input type="text" required value={form.client_prenom}
                  onChange={e => setForm(f => ({ ...f, client_prenom: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="Prenom" data-testid="checkout-client-prenom"
                />
              </div>
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Nom *</label>
                <input type="text" required value={form.client_nom}
                  onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="Nom" data-testid="checkout-client-nom"
                />
              </div>
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Email *</label>
                <input type="email" required value={form.client_email}
                  onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="votre@email.com" data-testid="checkout-client-email"
                />
              </div>
            </div>

            {/* Beneficiaire */}
            <p className="text-xs font-['Manrope',sans-serif] text-gray-400 uppercase tracking-wider mb-3">Beneficiaire</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Prenom</label>
                <input type="text" value={form.beneficiaire_prenom}
                  onChange={e => setForm(f => ({ ...f, beneficiaire_prenom: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="Prenom" data-testid="checkout-beneficiaire-prenom"
                />
              </div>
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Nom</label>
                <input type="text" value={form.beneficiaire}
                  onChange={e => setForm(f => ({ ...f, beneficiaire: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="Nom" data-testid="checkout-beneficiaire"
                />
              </div>
              <div>
                <label className="text-xs font-['Manrope',sans-serif] text-gray-500 mb-1 block">Message personnalise</label>
                <input type="text" value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20"
                  placeholder="Bon anniversaire !" data-testid="checkout-message"
                />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm font-['Manrope',sans-serif]" data-testid="checkout-error">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="bg-gray-50 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              {displayAmount > 0 && (
                <div className="text-center sm:text-left">
                  <p className="text-gray-400 text-xs font-['Manrope',sans-serif] uppercase tracking-wider">Total</p>
                  <p className="text-3xl font-light text-[#1a2744]">{displayAmount}<span className="text-lg text-[#0d9488] ml-1">€</span></p>
                </div>
              )}
            </div>
            <button type="submit" disabled={loading || (!selected)}
              className="flex items-center gap-3 bg-[#1a2744] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-submit-btn"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Redirection...</>
              ) : (
                <><Gift size={16} /> Offrir ce bon cadeau <ArrowRight size={14} /></>
              )}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 font-['Manrope',sans-serif]">
            Paiement securise par Stripe. Le bon cadeau sera envoye par email apres confirmation du paiement.
          </p>
        </form>
      </div>

      {/* Info Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-center text-3xl font-light mb-10">Comment ca marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Choisissez', desc: 'Selectionnez le montant et personnalisez votre cadeau' },
              { step: '2', title: 'Payez en ligne', desc: 'Paiement 100% securise par carte bancaire via Stripe' },
              { step: '3', title: 'Recevez par email', desc: 'Le bon cadeau PDF est envoye instantanement par email' },
            ].map((s, i) => (
              <div key={i} className="text-center" data-testid={`how-step-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#1a2744] text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">{s.step}</div>
                <h3 className="text-xl mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ CONFIRMATION PAGE ══════
function BonsCadeauxConfirmation() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [bon, setBon] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/bons-cadeaux/checkout/status/${sessionId}`);
        const data = await res.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setBon(data.bon);
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setAttempts(prev => {
          if (prev >= 8) {
            setStatus('timeout');
            return prev;
          }
          setTimeout(poll, 2500);
          return prev + 1;
        });
      } catch {
        setAttempts(prev => {
          if (prev >= 8) {
            setStatus('error');
            return prev;
          }
          setTimeout(poll, 3000);
          return prev + 1;
        });
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',Georgia,serif]" data-testid="bons-cadeaux-confirmation">
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="pt-24 pb-16 min-h-[70vh] flex items-center justify-center">
        <div className="max-w-xl mx-auto px-4 text-center w-full">
          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 size={48} className="mx-auto mb-6 text-[#1a2744] animate-spin" />
              <h1 className="text-3xl font-light mb-3">Verification du paiement...</h1>
              <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">Veuillez patienter quelques instants</p>
            </div>
          )}

          {status === 'success' && bon && (
            <div data-testid="payment-success">
              {/* Decorative header */}
              <div className="relative mb-8">
                <div className="w-24 h-24 rounded-full bg-[#0d9488]/10 flex items-center justify-center mx-auto">
                  <div className="w-16 h-16 rounded-full bg-[#0d9488]/20 flex items-center justify-center">
                    <Check size={32} className="text-[#0d9488]" />
                  </div>
                </div>
              </div>

              <h1 className="text-4xl font-light mb-2 text-[#1a2744]">Merci !</h1>
              <p className="text-gray-400 text-sm font-['Manrope',sans-serif] mb-10 uppercase tracking-[3px]">Votre cadeau est pret</p>

              {/* Gift card visual */}
              <div className="relative bg-[#1a2744] rounded-2xl p-8 mb-10 text-left overflow-hidden" data-testid="bon-details">
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#0d9488]/10 rounded-full -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#0d9488]/5 rounded-full translate-y-1/2 -translate-x-1/4" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-[#0d9488] text-[9px] font-['Manrope',sans-serif] tracking-[3px] uppercase mb-1">Bon Cadeau</p>
                      <p className="text-white/40 text-xs font-['Manrope',sans-serif] font-mono">{bon.numero}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl text-white font-light">{bon.montant}<span className="text-lg text-[#0d9488] ml-1">€</span></p>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-5 grid grid-cols-2 gap-4">
                    {bon.beneficiaire && (
                      <div>
                        <p className="text-[#0d9488] text-[9px] font-['Manrope',sans-serif] tracking-[2px] uppercase mb-1">Pour</p>
                        <p className="text-white text-sm font-['Manrope',sans-serif]">{bon.beneficiaire}</p>
                      </div>
                    )}
                    {bon.client_nom && (
                      <div>
                        <p className="text-[#0d9488] text-[9px] font-['Manrope',sans-serif] tracking-[2px] uppercase mb-1">De la part de</p>
                        <p className="text-white text-sm font-['Manrope',sans-serif]">{bon.client_nom}</p>
                      </div>
                    )}
                    {bon.message && (
                      <div className="col-span-2">
                        <p className="text-[#0d9488] text-[9px] font-['Manrope',sans-serif] tracking-[2px] uppercase mb-1">Message</p>
                        <p className="text-white/80 text-sm font-['Manrope',sans-serif] italic">"{bon.message}"</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[#0d9488] text-[9px] font-['Manrope',sans-serif] tracking-[2px] uppercase mb-1">Valable jusqu'au</p>
                      <p className="text-white text-sm font-['Manrope',sans-serif]">{bon.date_expiration}</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10">
                    <p className="text-white/30 text-[10px] font-['Manrope',sans-serif] text-center">Restaurant Le Mas — 6 Av. Marechal Joffre, Saint-Laurent-de-la-Salanque</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-400 text-sm font-['Manrope',sans-serif] mb-8">
                Le bon cadeau en PDF a ete envoye a <strong className="text-[#1a2744]">{bon.client_email}</strong>
              </p>

              <button onClick={() => nav('/site/restaurant')}
                className="bg-[#1a2744] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors"
                data-testid="back-to-home-btn"
              >
                Retour a l'accueil
              </button>
            </div>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <Gift size={40} className="text-red-400" />
              </div>
              <h1 className="text-3xl font-light mb-3 text-gray-800">
                {status === 'expired' ? 'Session expiree' : 'Un probleme est survenu'}
              </h1>
              <p className="text-gray-400 text-base font-['Manrope',sans-serif] mb-8">
                {status === 'expired'
                  ? 'La session de paiement a expire. Veuillez reessayer.'
                  : 'Nous n\'avons pas pu confirmer votre paiement. Si le montant a ete debite, contactez-nous.'}
              </p>
              <div className="flex gap-4 justify-center">
                <button onClick={() => nav('/site/restaurant/bons-cadeaux')}
                  className="bg-[#1a2744] text-white px-8 py-3 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors"
                  data-testid="retry-btn"
                >
                  Reessayer
                </button>
                <button onClick={() => nav('/site/restaurant/contact')}
                  className="border-2 border-[#1a2744] text-[#1a2744] px-8 py-3 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#1a2744]/5 transition-colors"
                  data-testid="contact-btn"
                >
                  Contactez-nous
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <SiteFooter section="restaurant" />
    </div>
  );
}

export { BonsCadeauxShop, BonsCadeauxConfirmation };
